import React, { useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { FileText, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';

// ── Clinical zone definitions ────────────────────────────────────────────────
// Source: Dion Hair Clinic reference chart
// Note: Area 1 (Geheimratsecken) and Area 7 (Crown Übergang) are bilateral

const ZONES_DATA = {
  haar: {
    title: 'Haartransplantation',
    refImage: 'https://customer-assets.emergentagent.com/job_e1aeaea2-7c28-4f84-ba46-647d565b65b1/artifacts/zqywqtl1_image.png',
    zones: [
      { id: 'area1', label: 'Geheimratsecken',       area: 'Area 1', grafts: 600,  note: 'bilateral' },
      { id: 'area2', label: 'Haarlinie / Front',      area: 'Area 2', grafts: 2000, note: '' },
      { id: 'area3', label: 'Forelock (Frontzentrum)',area: 'Area 3', grafts: 700,  note: '' },
      { id: 'area4', label: 'Vorderer Midscalp',      area: 'Area 4', grafts: 1600, note: '' },
      { id: 'area5', label: 'Midscalp Zentrum',       area: 'Area 5', grafts: 1800, note: '' },
      { id: 'area6', label: 'Vertex (Crown Zentrum)', area: 'Area 6', grafts: 1000, note: '' },
      { id: 'area7', label: 'Crown Übergang',         area: 'Area 7', grafts: 400,  note: 'bilateral' },
    ],
  },
  bart: {
    title: 'Barttransplantation',
    refImage: null,
    zones: [
      { id: 'koteletten_l', label: 'Koteletten L',  area: '',  grafts: 150, note: '' },
      { id: 'koteletten_r', label: 'Koteletten R',  area: '',  grafts: 150, note: '' },
      { id: 'wange_l',      label: 'Wange L',        area: '',  grafts: 300, note: '' },
      { id: 'wange_r',      label: 'Wange R',        area: '',  grafts: 300, note: '' },
      { id: 'schnurrbart',  label: 'Schnurrbart',    area: '',  grafts: 150, note: '' },
      { id: 'kinn',         label: 'Kinnbart',       area: '',  grafts: 300, note: '' },
    ],
  },
  augenbrauen: {
    title: 'Augenbrauentransplantation',
    refImage: null,
    zones: [
      { id: 'braue_l',  label: 'Braue Links',  area: '', grafts: 200, note: '' },
      { id: 'braue_r',  label: 'Braue Rechts', area: '', grafts: 200, note: '' },
    ],
  },
};

export function GraftCalculator({ treatment = 'haar', onInsert, onGraftChange }) {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [imgExpanded, setImgExpanded] = useState(false);
  const data = ZONES_DATA[treatment] || ZONES_DATA.haar;

  const [selected, setSelected] = useState(new Set());
  const [customGrafts, setCustomGrafts] = useState({});

  const toggleZone = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const getGrafts = (zone) => customGrafts[zone.id] ?? zone.grafts;

  const adjustGrafts = (id, delta) => {
    const base = customGrafts[id] ?? data.zones.find(z => z.id === id).grafts;
    setCustomGrafts(prev => ({ ...prev, [id]: Math.max(50, base + delta) }));
  };

  const selectedZones = useMemo(
    () => data.zones.filter(z => selected.has(z.id)),
    [data.zones, selected]
  );

  const total = useMemo(
    () => selectedZones.reduce((sum, z) => sum + getGrafts(z), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedZones, customGrafts]
  );

  // Notify parent of current graft summary whenever selection changes
  React.useEffect(() => {
    if (!onGraftChange) return;
    if (selectedZones.length === 0) {
      onGraftChange(null);
    } else {
      const lines = selectedZones.map(z => `• ${z.label}: ca. ${getGrafts(z).toLocaleString('de-DE')} Grafts`).join('\n');
      onGraftChange(`${data.title} – Graft-Kalkulation:\n${lines}\nGesamt: ca. ${total.toLocaleString('de-DE')} Grafts`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZones, customGrafts, total]);

  const handleInsert = () => {
    if (!selectedZones.length) return;
    const lines = selectedZones
      .map(z => `  • ${z.label}: ca. ${getGrafts(z).toLocaleString('de-DE')} Grafts`)
      .join('\n');
    onInsert(`\n\nGraft-Kalkulation (${data.title}):\n${lines}\n\nGeschätzte Gesamtanzahl: ca. ${total.toLocaleString('de-DE')} Grafts\n`);
  };

  const accent = isDark ? '#7BA7C2' : '#5A8BA6';

  return (
    <div className={`rounded-xl border mt-3 overflow-hidden ${isDark ? 'border-white/8 bg-[#0e0e0e]' : 'border-gray-200 bg-white'}`}>

      {/* ── Toggle header ── */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${isDark ? 'hover:bg-white/5 text-white/70' : 'hover:bg-gray-50 text-gray-600'}`}
        data-testid="graft-calc-toggle"
      >
        <span className="flex items-center gap-2">
          {/* small icon */}
          <svg viewBox="0 0 16 16" className="w-4 h-4 shrink-0" fill="none">
            <circle cx="8" cy="8" r="6.5" stroke={accent} strokeWidth="1.5" />
            <circle cx="8" cy="5.5" r="1.8" stroke={accent} strokeWidth="1.2" />
            <path d="M 4.5 13 Q 4.5 10.5 8 10.5 Q 11.5 10.5 11.5 13" stroke={accent} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          </svg>
          Graftrechner
          {selected.size > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
              style={{ background: isDark ? 'rgba(123,167,194,0.15)' : 'rgba(90,139,166,0.1)', color: accent }}>
              ca. {total.toLocaleString('de-DE')} Grafts
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* ── Body ── */}
      {open && (
        <div className={`px-4 pb-4 pt-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>

            {/* ── Reference image toggle ── */}
            {data.refImage && (
              <div className="mb-3">
                <button
                  onClick={() => setImgExpanded(v => !v)}
                  className={`flex items-center gap-1.5 text-[11px] font-medium mb-1.5 transition-colors ${isDark ? 'text-white/45 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                  data-testid="graft-ref-image-toggle"
                >
                  {imgExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  Referenzbild anzeigen
                </button>
                {imgExpanded && (
                  <div className={`rounded-lg overflow-hidden border mb-2 ${isDark ? 'border-white/8' : 'border-gray-200'}`}>
                    <img
                      src={data.refImage}
                      alt="Referenz Kopfbereiche"
                      className="w-full max-h-[280px] object-contain"
                      style={{ background: isDark ? '#1a1a1a' : '#f8f8f8' }}
                      data-testid="graft-ref-image"
                    />
                  </div>
                )}
              </div>
            )}

            {/* ── Zone list ── */}
            <div className="w-full">
              <p className={`text-[11px] font-medium mb-2 ${isDark ? 'text-white/35' : 'text-gray-400'}`}>Zonen & Graft-Anzahl</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {data.zones.map((zone, idx) => {
                  const on = selected.has(zone.id);
                  const grafts = getGrafts(zone);
                  return (
                    <div
                      key={zone.id}
                      onClick={() => toggleZone(zone.id)}
                      className={`flex items-center gap-2 px-3 py-3 rounded-lg border cursor-pointer transition-all select-none ${
                        on
                          ? (isDark ? 'border-[#7BA7C2]/40 bg-[#7BA7C2]/10' : 'border-[#5A8BA6]/35 bg-[#5A8BA6]/7')
                          : (isDark ? 'border-white/5 bg-white/2 opacity-55' : 'border-gray-100 bg-gray-50/60 opacity-55')
                      }`}
                      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                      data-testid={`zone-row-${zone.id}`}
                    >
                      {/* Zone number badge */}
                      <span
                        className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 transition-colors"
                        style={{
                          background: on ? accent : (isDark ? '#2a2a2a' : '#e8e8e8'),
                          color: on ? '#fff' : (isDark ? '#555' : '#aaa'),
                        }}
                      >
                        {idx + 1}
                      </span>

                      <span className={`text-xs flex-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{zone.label}</span>

                      {/* Graft adjuster */}
                      <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => adjustGrafts(zone.id, -50)}
                          disabled={!on}
                          className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 active:bg-white/20' : 'hover:bg-gray-200 text-gray-400 active:bg-gray-300'} disabled:pointer-events-none`}
                          style={{ touchAction: 'manipulation' }}
                          data-testid={`zone-minus-${zone.id}`}
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className={`text-xs font-mono w-[56px] text-center ${on ? (isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]') : (isDark ? 'text-white/20' : 'text-gray-300')}`}>
                          ~{grafts.toLocaleString('de-DE')}
                        </span>
                        <button
                          onClick={() => adjustGrafts(zone.id, 50)}
                          disabled={!on}
                          className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 active:bg-white/20' : 'hover:bg-gray-200 text-gray-400 active:bg-gray-300'} disabled:pointer-events-none`}
                          style={{ touchAction: 'manipulation' }}
                          data-testid={`zone-plus-${zone.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          {/* Footer */}
          <div className={`mt-4 pt-3 border-t flex items-center justify-between gap-3 ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <div className="flex items-baseline gap-2">
              <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Gesamt:</span>
              <span className="text-xl font-bold" style={{ color: accent }} data-testid="graft-total">
                {selected.size > 0 ? `ca. ${total.toLocaleString('de-DE')}` : '–'}
              </span>
              {selected.size > 0 && (
                <span className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
                  Grafts ({selected.size} Zone{selected.size !== 1 ? 'n' : ''})
                </span>
              )}
            </div>
            <Button
              onClick={handleInsert}
              disabled={selected.size === 0}
              className="text-xs h-8 bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] text-white disabled:opacity-40 shrink-0"
              data-testid="graft-insert-btn"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" />
              In Antwort einfügen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GraftCalculator;
